import type { Viewpoint } from '@viewer/lib/viewpoint'
import { corDoBcf, lerCores } from './bcfVisinfo'

/**
 * Os elementos ligados a um tópico BCF, sem nada à volta.
 *
 * Vêm da selecção (`components.selected_global_ids`) de cada viewpoint
 * anexado ao tópico: é aí que o BCF diz de que elementos o tópico fala.
 * Ficam sem repetidos e pela ordem em que aparecem, cada um com os
 * viewpoints de onde vem e com a sua cor.
 *
 * Puro: sem viewer e sem store. A resolução nos modelos abertos, a
 * selecção, o realce e a caixa de corte estão em `bcfElementosCena.ts` e
 * `bcfRealce.ts`.
 */

/**
 * Paleta para os elementos sem cor no ficheiro.
 *
 * Oito cores escolhidas para se distinguirem bem umas das outras
 * (distância CIELAB de pelo menos 40 entre quaisquer duas) e das que o
 * viewer já usa com outro sentido (pelo menos 30): o âmbar da selecção
 * (#f59e0b) e da pré-selecção (#fbbf24), o ciano (#22d3ee) e o magenta
 * (#e879f9) dos conflitos, e os cinzentos do fantasma (#9ca3af) e da
 * transparência (#cbd5e1). O teste confirma as duas distâncias.
 */
export const PALETA_ELEMENTOS: readonly string[] = [
  '#1f77b4', // azul
  '#2ca02c', // verde
  '#d62728', // vermelho
  '#006d5b', // verde-petróleo
  '#4b0082', // índigo
  '#f4a6c6', // rosa
  '#e7298a', // framboesa
  '#a6761d', // ocre
]

/** As cores que o viewer já usa com outro sentido. */
export const CORES_RESERVADAS: readonly string[] = [
  '#f59e0b',
  '#fbbf24',
  '#22d3ee',
  '#e879f9',
  '#9ca3af',
  '#cbd5e1',
]

export interface ElementoLigado {
  guid: string
  /** Os viewpoints do tópico que o seleccionam, pela ordem do tópico. */
  viewpoints: string[]
  /** `#rrggbb`. */
  cor: string
  /** Verdadeiro quando a cor vem do `Coloring` do `.bcfv`. */
  corDoFicheiro: boolean
}

/** O mínimo de um viewpoint anexado de que isto precisa. */
export interface ViewpointAnexado {
  id: string
  viewpoint: Pick<Viewpoint, 'components' | 'bcfv_original'>
}

/** As cores que um viewpoint dá aos seus componentes: as do próprio
 *  viewpoint (`components.coloring`, que a exportação dos conflitos
 *  preenche) e, para os que lá não estão, as do `.bcfv` original de um
 *  BCF importado. */
function coresDoViewpoint(vp: ViewpointAnexado): Map<string, string> {
  const cores = new Map<string, string>()
  const proprias = (vp.viewpoint.components as {
    coloring?: readonly { color: string; global_ids: readonly string[] }[]
  }).coloring
  for (const grupo of proprias ?? []) {
    const hex = corDoBcf(grupo.color)
    if (!hex) continue
    for (const g of grupo.global_ids) if (!cores.has(g)) cores.set(g, hex)
  }
  const xml = vp.viewpoint.bcfv_original?.xml
  if (xml) for (const [g, hex] of lerCores(xml)) if (!cores.has(g)) cores.set(g, hex)
  return cores
}

export function elementosDoTopico(
  anexados: readonly ViewpointAnexado[],
): ElementoLigado[] {
  const porGuid = new Map<string, { viewpoints: string[]; cor: string | null }>()
  for (const vp of anexados) {
    const cores = coresDoViewpoint(vp)
    for (const guid of vp.viewpoint.components.selected_global_ids) {
      if (!guid) continue
      const e = porGuid.get(guid)
      if (e) {
        if (!e.viewpoints.includes(vp.id)) e.viewpoints.push(vp.id)
        e.cor ??= cores.get(guid) ?? null
      } else {
        porGuid.set(guid, { viewpoints: [vp.id], cor: cores.get(guid) ?? null })
      }
    }
  }
  let k = 0
  return [...porGuid].map(([guid, e]) => {
    if (e.cor) return { guid, viewpoints: e.viewpoints, cor: e.cor, corDoFicheiro: true }
    const cor = PALETA_ELEMENTOS[k % PALETA_ELEMENTOS.length]
    k += 1
    return { guid, viewpoints: e.viewpoints, cor, corDoFicheiro: false }
  })
}

export interface Caixa {
  min: [number, number, number]
  max: [number, number, number]
}

/** Folga mínima da caixa de corte, em metros. */
export const FOLGA_MINIMA = 0.5
/** Folga relativa, em fracção da dimensão em cada eixo. */
export const FOLGA_RELATIVA = 0.1

/**
 * A caixa de corte à volta dos elementos: a caixa envolvente com uma
 * folga em cada eixo de 0,5 m ou 10 % da dimensão nesse eixo, a que for
 * maior. Por eixo, para um elemento achatado (uma laje) não ficar com a
 * caixa tão alta como larga.
 */
export function caixaComFolga(c: Caixa): Caixa {
  const min: [number, number, number] = [0, 0, 0]
  const max: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < 3; i++) {
    const folga = Math.max(FOLGA_MINIMA, (c.max[i] - c.min[i]) * FOLGA_RELATIVA)
    min[i] = c.min[i] - folga
    max[i] = c.max[i] + folga
  }
  return { min, max }
}

/** A caixa em extensões da caixa de corte (orientação identidade, em que
 *  as extensões são as coordenadas do mundo). */
export function extensoesDe(c: Caixa) {
  return {
    minX: c.min[0],
    maxX: c.max[0],
    minY: c.min[1],
    maxY: c.max[1],
    minZ: c.min[2],
    maxZ: c.max[2],
  }
}

/** O intervalo dos cursores da caixa de corte: a caixa dos elementos
 *  unida à dos modelos, para se poder alargar o corte até ao modelo
 *  inteiro. Sem caixa dos modelos, a dos elementos. */
export function intervaloDosCursores(elementos: Caixa, modelos: Caixa | null): Caixa {
  if (!modelos) return elementos
  return {
    min: [0, 1, 2].map((i) => Math.min(elementos.min[i], modelos.min[i])) as [number, number, number],
    max: [0, 1, 2].map((i) => Math.max(elementos.max[i], modelos.max[i])) as [number, number, number],
  }
}
